import React from "react";
import { createRoot } from "react-dom/client";
import { legalLinks } from "./data.jsx";
import { useApp, AppProvider } from "./store.jsx";
import { Header, VerifyBanner, ImportStrip, DrawStrip, TabBar, ToastHost, SupportDialog, SupportStrip } from "./chrome.jsx";
import { BrowseScreen } from "./browse.jsx";
import { ListingScreen } from "./listing.jsx";
import { TraderScreen } from "./trader.jsx";
import { TradesScreen } from "./trades.jsx";
import { InboxScreen } from "./inbox.jsx";
import { YouScreen } from "./you.jsx";
import { PostScreen } from "./post.jsx";
import { ShopScreen } from "./shop.jsx";
import { ShopShell } from "./shop-dash.jsx";
import { AdminScreen } from "./admin.jsx";
import { ImportScreen } from "./import.jsx";
import { OnboardingScreen } from "./onboarding.jsx";
import { InviteScreen } from "./referral.jsx";
import { CampaignScreen, InvitedScreen } from "./campaign.jsx";
import { RevealOverlay } from "./reveal.jsx";
import { VerifyScreen } from "./verify.jsx";
import { ForgotScreen, ResetScreen } from "./password.jsx";
import { installWorker } from "./push.jsx";
import { PushInvite } from "./push-invite.jsx";

/** Screens that are inherently about YOUR account, and mean nothing to a guest. */
const PERSONAL = new Set(['trades', 'inbox', 'you', 'post', 'import', 'admin', 'invite', 'shop', 'dashboard']);

function App() {
  const { route, booting, trader, requireAuth } = useApp();

  // Showing a guest an empty Trades tab implies they have no trades, when the truth
  // is they have no account. Ask, and say why.
  //
  // The redirect happens in an effect rather than during render: navigating IS a
  // state change, and doing it mid-render is how you get a render loop. `replace`
  // keeps the blocked screen out of history, so leaving the form goes back to
  // browsing rather than bouncing off the guard again.
  //
  // Declared BEFORE the early returns, and guarded on `booting` inside instead.
  // A hook that only runs on some renders changes the hook ORDER between them,
  // which is how React starts handing one hook's state to another — and the
  // symptom shows up somewhere else entirely.
  const blocked = !booting && !trader && PERSONAL.has(route.name);
  React.useEffect(() => {
    if (!blocked) return;
    requireAuth(route.name === 'post' ? 'post a card'
      // "see your import" is what the fallback would have produced, which is not
      // a thing anybody has.
      : route.name === 'import' ? 'import your cards'
      // The dashboard's route name is not a word anybody would use about
      // themselves: "see your dashboard" is furniture, "see your shop" is the
      // thing they came for.
      : route.name === 'dashboard' ? 'see your shop'
      : 'see your ' + route.name, { replace: true });
  }, [blocked, route.name, requireAuth]);

  /*
   * The wordmark until the server has said whether anybody is signed in.
   *
   * NOT NOTHING, WHICH IS WHAT IT WAS. Guessing the answer flashes the wrong
   * screen at a returning trader, and that reasoning still stands — but
   * rendering `null` meant the page had no first paint at all until a request
   * came back, and the Android app keeps its splash screen up until the page
   * paints. Reported as an app stuck on the splash forever.
   *
   * This is the same markup index.html already draws, so React replacing one
   * with the other changes nothing on screen: the wordmark is there from the
   * first byte and stays until there is something truer to show.
   */
  if (booting) return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--kraft-50, #FAF7F1)' }}>
      <div style={{ font: "700 1.35rem/1.2 system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif", letterSpacing: '-.02em', color: '#2B2724' }}>
        cardboard<span style={{ color: '#E8552A' }}>market</span>
      </div>
    </div>
  );
  if (route.name === 'onboarding') return <><OnboardingScreen /><ToastHost /></>;
  /*
   * The invite landing page takes the whole window, like onboarding — it is the
   * first thing somebody sees of this product, and wrapping it in a header with
   * Browse and Inbox in it would be showing a stranger the furniture of an app
   * they have not joined.
   */
  if (route.name === 'invited') return <><InvitedScreen /><ToastHost /></>;
  /*
   * THE SHOP'S BACK OFFICE TAKES THE WHOLE WINDOW, like onboarding does.
   *
   * It is a shell rather than a screen: a dark rail that IS the navigation for
   * as long as somebody is working as the shop. Rendering it inside the app's
   * header and tab bar would put Browse and Nearby above a shop's stock table —
   * two products on one page, and the reason the owner said the first version
   * "doesn't look anything like" his design.
   */
  /*
   * AND A SHOP NEVER LEAVES IT. Every route a shop account can reach renders
   * inside the rail, and the ones it has no business on send it back to its
   * counter.
   *
   * The owner's reasoning, and it is about what the words on a screen MEAN
   * rather than about tidiness: "a shop does not need to browse, see nearby
   * section or trades section... It's more a trades receiver only. Doesn't also
   * need to see the whole header with the pin, post a card button or the
   * profile button."
   *
   * He is right that it was half-done before this, which is the worst of the
   * three states: a shop that can wander into Browse is a shop that will, and
   * then Post a card means one thing from the rail and another from the header.
   *
   * HIDING THE LINKS WOULD NOT BE THE BOUNDARY — a typed address is a link
   * somebody did not need. So the test is on the route, here, where every one
   * of them passes.
   */
  if (trader && trader.kind === 'shop') return <><ShopShell /><ToastHost /></>;
  /*
   * And the counter's own address, reached by somebody who is NOT the shop: a
   * member who has not put the apron on, or anybody who typed it. The shell
   * answers that itself — it offers the switch, or says there is no shop — so
   * the route needs no second opinion about who is allowed.
   */
  if (route.name === 'dashboard') return <><ShopShell /><ToastHost /></>;
  if (blocked) return null;
  const Screen = {
    browse: BrowseScreen, listing: ListingScreen,
    trades: TradesScreen, inbox: InboxScreen, you: YouScreen,
    post: PostScreen, import: ImportScreen, verify: VerifyScreen,
    forgot: ForgotScreen, reset: ResetScreen,
    admin: AdminScreen, trader: TraderScreen, shop: ShopScreen,
    invite: InviteScreen, campaign: CampaignScreen,
  }[route.name] || BrowseScreen;
  /*
   * The inbox is the one screen whose CONTENT is bounded by the window rather
   * than growing past it — see `.cbm-shell--inbox` in app.css. It is set HERE
   * because that is where the shell is: a conversation cannot bound a page it is
   * three levels inside of, and the alternative is a screen reaching up to put a
   * class on somebody else's element.
   *
   * Named by route only. Whether the layout is actually the two-pane one is a
   * width question, and the stylesheet is where width questions are answered —
   * below 1000px this class does nothing at all.
   */
  return (
    <div className={'cbm-shell' + (route.name === 'inbox' ? ' cbm-shell--inbox' : '')}>
      {/*
       * The chrome and the screen, wrapped; the footer deliberately left out.
       *
       * This div is what the inbox sizes to the window. Header, banner and main
       * were siblings of the footer, so a shell one viewport tall was a viewport
       * with the footer INSIDE it, eating the conversation — and the only way to
       * keep them siblings and still put the footer below the fold is to subtract
       * a header height that is 56 or 57 depending on the viewport and is not
       * even there when the banner is. Grouping the three removes the sum: the
       * group is the viewport, `flex:1` shares it out, and the footer is simply
       * the next thing down the page.
       *
       * Every screen renders it. It changes nothing outside the inbox — the
       * wrapper is `flex:1` and passes that straight to `.cbm-main`, which is
       * what used to hold it.
       */}
      <div className="cbm-shell__above">
        <Header />
        <VerifyBanner />
        {/* A background import, said out loud wherever you happen to be — the
            slow path's whole offer is that you went somewhere else. */}
        <ImportStrip />
        {/* An open draw, wherever you are. It was in browse's filter rail,
            which does not render below 1000px — so on a phone it was not
            buried, it was absent. */}
        <DrawStrip />
        <main className="cbm-main"><Screen key={route.name + (route.id || '') + (route.tab || '')} /></main>
      </div>
      <SiteFooter />
      {/* Mobile-only: the footer above is hidden below 760px, so without this
          there is no way to reach the legal pages or report a bug from a phone. */}
      <SupportStrip />
      <TabBar />
      {/* Over everything, and only when somebody asked for it. It renders
          nothing at all until `checkResult` names a draw. */}
      <RevealOverlay />
      {/* Asks WHY before Android asks WHETHER — once, to a signed-in person, in
          the app only. On Android 13 a refusal is close to permanent, so the
          system prompt is never raised until somebody has read what we would
          send and tapped a button saying to send it. See push-invite.jsx. */}
      <PushInvite />
      <ToastHost />
    </div>
  );
}

function SiteFooter() {
  const { Wordmark, FluteDivider, Icon } = window.CardboardmarketDesignSystem_2a3239;
  const { go, trader, locale, t } = useApp();
  const [support, setSupport] = React.useState(false);
  return (
    <footer className="cbm-desk" style={{ background: 'var(--bg-inverse)', color: 'var(--kraft-300)', marginTop: 'var(--space-12)' }}>
      <FluteDivider tone="brand" />
      <div style={{ maxWidth: 'var(--container-max)', margin: '0 auto', padding: 'var(--space-9) var(--gutter-desktop)', display: 'flex', gap: 'var(--space-9)', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 320 }}>
          <Wordmark size={16} tone="inverse" />
          <span style={{ font: 'var(--type-body-sm)', color: 'var(--ink-300)' }}>{t("Proximity instead of postage. Find the card, meet the holder, hand it over.")}</span>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-9)', flexWrap: 'wrap' }}>
          {/* The "You" column is personal, so a guest is not offered it here either
              — hiding it in the nav and leaving it in the footer would just move the
              dead end further down the page. */}
          {[
            // Keys stay English and stable; only what is rendered goes through t().
            // Keying a list on its translated label means every element unmounts and
            // remounts when somebody changes language, for no reason.
            ['Market', [['Browse', 'browse']]],
            ...(trader ? [['You', [['Your trades', 'trades'], ['Inbox', 'inbox'], ['Profile', 'you']]]] : []),
          ].map(([title, links]) => (
            <div key={title} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span className="cbm-label" style={{ color: 'var(--kraft-400)' }}>{t(title)}</span>
              {links.map(([label, r]) => (
                <button key={r} onClick={() => go(r)} style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', font: 'var(--type-ui)', color: 'var(--kraft-200)' }}>{t(label)}</button>
              ))}
            </div>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span className="cbm-label" style={{ color: 'var(--kraft-400)' }}>{t("Legal")}</span>
            {/* Per locale — see legalLinks. An English reader was being sent to
                the Spanish policy, which is not a policy they were shown. */}
            {legalLinks(locale).map(([label, href]) => (
              <a key={href} href={href} style={{ font: 'var(--type-ui)', color: 'var(--kraft-200)', textDecoration: 'none' }}>{label}</a>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span className="cbm-label" style={{ color: 'var(--kraft-400)' }}>{t("Help")}</span>
            <button onClick={() => setSupport(true)} style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', font: 'var(--type-ui)', color: 'var(--kraft-200)' }}>{t("Report a problem")}</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span className="cbm-label" style={{ color: 'var(--kraft-400)' }}>{t("Safety")}</span>
            <span style={{ font: 'var(--type-body-sm)', color: 'var(--ink-300)', maxWidth: 240 }}>{t("Meet in public, check the card before you pay, mark the trade done together.")}</span>
          </div>
        </div>
      </div>
      <SupportDialog open={support} onClose={() => setSupport(false)} />
    </footer>
  );
}

/*
 * The root, and the end of the line.
 *
 * `App` and `SiteFooter` were on `window` because everything was; nothing has
 * ever imported this file except main.js, and it imports it for this render.
 * There is nothing left for either name to be exported TO.
 */
createRoot(document.getElementById('root')).render(
  <AppProvider><App /></AppProvider>
);

/*
 * AFTER the first render, deliberately. Registering a worker is not urgent and
 * the first paint is: it costs a request and a little main-thread time, neither
 * of which should come before somebody sees the app.
 *
 * What it buys is the offline page — which matters most in the Android app,
 * where a failed navigation otherwise shows Chrome's error page wearing our
 * icon. See sw.js.
 */
installWorker();
